import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent,
} from "react";

interface Size {
  readonly width: number;
  readonly height: number;
}

/** 既定のパネルサイズ（px）。 */
const DEFAULT_SIZE: Size = { width: 400, height: 560 };
/** 利用者が縮められる下限（px）。 */
const MIN_SIZE_PX = 320;
/** localStorage から復元してよいサイズの上限（px）。 */
const MAX_SIZE_PX = 4000;
/** 画面左右に残す余白（px）。 */
const VIEWPORT_MARGIN_X_PX = 32;
/** リサイズ操作で許す高さの上限を決める余白（px）。 */
const RESIZE_MARGIN_Y_PX = 92;
/**
 * 実際に表示する高さの上限を決める余白（px）。
 * リサイズ側の余白より大きい（＝厳しい）ため、表示はこちらで頭打ちになる。
 * 保存値がこの上限を超えても、画面が広がれば元の大きさへ戻せるよう保存値自体は切り詰めない。
 */
const DISPLAY_MARGIN_Y_PX = 100;
/** リサイズハンドルを出す最小の画面幅（px）。これ未満では幅いっぱいで表示する。 */
export const RESIZABLE_MIN_VIEWPORT_WIDTH_PX = 600;

interface LayoutOptions {
  /** 子から通知された内容の高さ（px）。未指定なら従来どおり固定高さで表示する。 */
  readonly contentHeight?: number | undefined;
}

/**
 * ミニウィンドウのサイズ・最大化・リサイズ・内容高さへの追随をまとめた hook。
 *
 * 配色・影・角丸は `styles.css` の CSS 変数側で指定し、ここでは寸法と位置だけを返す。
 */
export function useMiniWindowLayout(storageKey: string, options: LayoutOptions = {}) {
  // 保存キーはマウント中に固定され、初回だけ保存値を読み、その後は操作中のサイズを維持する。
  const [size, setSize] = useState(() => readSize(storageKey));
  const [maximized, setMaximized] = useState(false);
  const [resizing, setResizing] = useState(false);
  const [viewport, setViewport] = useState(readViewport);
  const drag = useRef<
    | {
        readonly x: number;
        readonly y: number;
        readonly size: Size;
        readonly direction: string;
      }
    | undefined
  >(undefined);

  useEffect(() => {
    const update = (): void => setViewport(readViewport());
    window.addEventListener("resize", update);
    window.visualViewport?.addEventListener("resize", update);
    window.visualViewport?.addEventListener("scroll", update);
    return () => {
      window.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("scroll", update);
    };
  }, []);

  const endResize = useCallback(() => {
    drag.current = undefined;
    setResizing(false);
  }, []);

  useEffect(() => {
    // ドラッグ中にウィンドウのフォーカスを失うと pointerup が届かないため、必ず終了させる。
    window.addEventListener("blur", endResize);
    return () => window.removeEventListener("blur", endResize);
  }, [endResize]);

  useEffect(() => {
    if (!resizing) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(size));
      } catch {
        // 保存不可（プライベートモード等）でも操作は継続する。
      }
    }
  }, [resizing, size, storageKey]);

  const startResize = useCallback(
    (event: PointerEvent<HTMLButtonElement>) => {
      if (viewport.width < RESIZABLE_MIN_VIEWPORT_WIDTH_PX || maximized || event.button !== 0) {
        return;
      }
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      drag.current = {
        x: event.clientX,
        y: event.clientY,
        size,
        direction: event.currentTarget.dataset.direction ?? "both",
      };
      setResizing(true);
    },
    [maximized, size, viewport.width]
  );

  const resize = useCallback(
    (event: PointerEvent<HTMLButtonElement>) => {
      const start = drag.current;
      if (!start) return;
      // パネルは右下固定のため、左・上へ引くほど大きくなる（差分の符号が反転する）。
      setSize({
        width: Math.max(
          MIN_SIZE_PX,
          Math.min(
            viewport.width - VIEWPORT_MARGIN_X_PX,
            start.size.width + (start.direction === "height" ? 0 : start.x - event.clientX)
          )
        ),
        height: Math.max(
          MIN_SIZE_PX,
          Math.min(
            viewport.height - RESIZE_MARGIN_Y_PX,
            start.size.height + (start.direction === "width" ? 0 : start.y - event.clientY)
          )
        ),
      });
    },
    [viewport]
  );

  const reset = useCallback(() => {
    setMaximized(false);
    setSize(DEFAULT_SIZE);
  }, []);

  const enlarge = useCallback(() => setMaximized((value) => !value), []);

  const wide = viewport.width < RESIZABLE_MIN_VIEWPORT_WIDTH_PX || maximized;
  const width = Math.min(
    wide ? viewport.width - VIEWPORT_MARGIN_X_PX : size.width,
    viewport.width - VIEWPORT_MARGIN_X_PX
  );
  const limit = Math.max(
    0,
    Math.min(
      maximized ? viewport.height - DISPLAY_MARGIN_Y_PX : size.height,
      viewport.height - DISPLAY_MARGIN_Y_PX
    )
  );
  const style: CSSProperties = {
    width: Math.max(0, width),
    // FAB（2.75rem）とその上下の間隔（16px + 12px）のぶんだけ持ち上げる。
    bottom: `calc(28px + 2.75rem + ${viewport.bottom}px + env(safe-area-inset-bottom, 0px))`,
    // 内容高さが分かっているときは高さを固定せず、上限だけを与えて内容に追随させる。
    ...(options.contentHeight === undefined ? { height: limit } : { maxHeight: limit }),
  };
  const frameStyle: CSSProperties | undefined =
    options.contentHeight === undefined
      ? undefined
      : { height: options.contentHeight, flex: "0 1 auto" };

  return {
    style,
    frameStyle,
    viewport,
    resizing,
    setResizing,
    maximized,
    reset,
    enlarge,
    startResize,
    resize,
    endResize,
  };
}

function readViewport(): { width: number; height: number; bottom: number } {
  const viewport = window.visualViewport;
  return {
    width: viewport?.width ?? window.innerWidth,
    height: viewport?.height ?? window.innerHeight,
    // 仮想キーボード等で可視領域が縮んだぶんだけ、パネルと FAB を押し上げる。
    bottom: viewport ? Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop) : 0,
  };
}

function readSize(storageKey: string): Size {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(storageKey) ?? "null");
    if (
      typeof value === "object" &&
      value !== null &&
      "width" in value &&
      "height" in value &&
      typeof value.width === "number" &&
      typeof value.height === "number" &&
      Number.isFinite(value.width) &&
      Number.isFinite(value.height) &&
      value.width >= MIN_SIZE_PX &&
      value.width <= MAX_SIZE_PX &&
      value.height >= MIN_SIZE_PX &&
      value.height <= MAX_SIZE_PX
    ) {
      return { width: value.width, height: value.height };
    }
  } catch {
    // 破損値・保存不可はいずれも既定サイズへ戻す。
  }
  return DEFAULT_SIZE;
}
